import React, { useId } from 'react';
import { useFormContext } from 'react-hook-form';
import { ArrowUpFromLine } from 'lucide-react';

import {
  CoverPhotoInput,
  CoverPhotoInputActions,
  CoverPhotoInputOpenAction,
  type FileError
} from '@epoch-ui/components/CoverPhotoInput';

import {
  FormField,
  FormLabel,
  FormDescription,
  FormControl,
  FormMessage,
  FormItem
} from '../../form';

export interface CoverPhotoFieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
  name: string;
  label?: string;
  description?: string;
  onUploadComplete: (file: File) => Promise<string>;
  onLoadingChange?: (isLoading: boolean) => void;
}

/**
 * CoverPhotoField is a form field component for handling cover photo uploads within a React Hook Form context.
 * It wraps the CoverPhotoInput component and integrates it with form validation and submission logic.
 *
 * This component should be used inside a React Hook Form context and provides form-specific
 * functionality such as error handling and form state management.
 *
 * In addition to the regular props for HTMLInputElement, it also accepts:
 *
 * @param {function} props.onUploadComplete - Callback function to handle the file upload.
 * It should return a Promise that resolves with the URL of the uploaded image.
 */
const CoverPhotoField: React.FC<CoverPhotoFieldProps> = ({
  name,
  label,
  description,
  onUploadComplete,
  onLoadingChange,
  ...props
}) => {
  const id = useId();
  const formCtx = useFormContext();

  const [isUploading, setIsUploading] = React.useState(false);

  // giving the parent an opportunity to know when an upload is in progress
  const handleUploadStart = (): void => {
    setIsUploading(true);
    if (onLoadingChange) onLoadingChange(true);
  };

  const handleUploadEnd = (): void => {
    setIsUploading(false);
    if (onLoadingChange) onLoadingChange(false);
  };

  return (
    <FormField
      name={name}
      control={formCtx.control}
      render={({ field: { onChange, value, ...field }, fieldState }): React.ReactElement => (
        <FormItem>
          {label && <FormLabel>{label}</FormLabel>}
          {description && <FormDescription>{description}</FormDescription>}
          <FormControl className="tw-mt-0">
            <CoverPhotoInput
              // Disable click and keyboard events on the photo preview
              // since the <CoverPhotoInputOpenAction /> is used below
              noClick
              noKeyboard
              {...props}
              {...field}
              id={id}
              photoUrl={value}
              onFileAccepted={async (file: File): Promise<void> => {
                formCtx.clearErrors(field.name);
                try {
                  handleUploadStart();
                  const url = await onUploadComplete(file);
                  onChange(url);
                } catch (error) {
                  console.error(error);
                  formCtx.setError(field.name, { message: 'Error uploading file' });
                } finally {
                  handleUploadEnd();
                }
              }}
              onFileRejected={(file: File, [{ message }]: FileError[]): void => {
                formCtx.setError(field.name, { message });
              }}
              error={!!fieldState.error}
              loading={isUploading}
            >
              <CoverPhotoInputActions>
                <CoverPhotoInputOpenAction>
                  <ArrowUpFromLine size={20} strokeWidth={3} />
                </CoverPhotoInputOpenAction>
              </CoverPhotoInputActions>
            </CoverPhotoInput>
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

export { CoverPhotoField };
